import * as borogove from "borogove";
import { useState } from "react";
import {
	Button,
	IconButton,
	Menu,
	MenuItem,
	TextField,
	Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const labels = {
	fn: "Display Name",
	pronouns: "Pronouns",
	tz: "Timezone",
	bday: "Birthdate",
	url: "Link",
	tel: "Phone Number",
	impp: "Chat Address",
	note: "Bio",
} as Record<string, string>;

const Item = ({
	setItem,
	item,
}: {
	setItem: (value: string) => void;
	item: borogove.ProfileItem;
}) => {
	const className = item.key;

	return (
		<>
			{item.text().map((value, idx) => (
				<TextField
					key={idx}
					required={true}
					label={labels[className] || className}
					value={value}
					onChange={(e) => setItem(e.target.value)}
					type={item.key === "email" ? "email" : "text"}
					fullWidth
				/>
			))}
			{item.key === "tel" &&
				item
					.uri()
					.map((value, idx) => (
						<TextField
							key={idx}
							required={true}
							label={labels[className] || className}
							value={value.replace(/^tel:/, "")}
							onChange={(e) => setItem("tel:" + e.target.value)}
							type="tel"
							fullWidth
						/>
					))}
			{item.key !== "tel" &&
				item
					.uri()
					.map((value, idx) => (
						<TextField
							key={idx}
							required={true}
							label={labels[className] || className}
							value={value}
							onChange={(e) => setItem(e.target.value)}
							type="url"
							fullWidth
						/>
					))}
			{item.date().map((value, idx) => (
				<TextField
					key={idx}
					required={true}
					label={labels[className] || className}
					value={value}
					onChange={(e) => setItem(e.target.value)}
					type="date"
					fullWidth
				/>
			))}
		</>
	);
};

const Row = ({
	item,
	setItem,
	removeItem,
}: {
	item: borogove.ProfileItem;
	setItem: (value: string) => void;
	removeItem: () => void;
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
	} = useSortable({ id: item.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<Stack
			key={item.id}
			direction="row"
			spacing={1}
			alignItems="center"
			ref={setNodeRef}
			style={style}
		>
			<IconButton
				size="small"
				ref={setActivatorNodeRef}
				title="Drag to order"
				{...attributes}
				{...listeners}
			>
				<FontAwesomeIcon icon={faGripVertical} />
			</IconButton>
			<Item item={item} setItem={setItem} />
			<IconButton size="small" title="Delete" onClick={removeItem}>
				<DeleteIcon />
			</IconButton>
		</Stack>
	);
};

export const EditProfile = ({
	profile,
	setProfile,
}: {
	profile: borogove.Profile;
	setProfile: (p: borogove.Profile) => void;
}) => {
	const [menu, setMenu] = useState<HTMLElement | undefined>();

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const addBlankItem = (k: string) => {
		const builder = new borogove.ProfileBuilder(profile);
		builder.add(k, "");
		setProfile(builder.build());
		setMenu(undefined);
	};

	return (
		<Stack spacing={2}>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={(e) => {
					if (!e.over || e.over.id === e.active.id) return;

					const builder = new borogove.ProfileBuilder(profile);
					builder.move("" + e.active.id, "" + e.over.id);
					setProfile(builder.build());
				}}
			>
				<SortableContext
					items={profile.items}
					strategy={verticalListSortingStrategy}
				>
					{profile.items.map((item) => (
						<Row
							key={item.id}
							item={item}
							setItem={(value) => {
								const builder = new borogove.ProfileBuilder(profile);
								builder.set(item.id, value);
								setProfile(builder.build());
							}}
							removeItem={() => {
								const builder = new borogove.ProfileBuilder(profile);
								builder.remove(item.id);
								setProfile(builder.build());
							}}
						/>
					))}
				</SortableContext>
			</DndContext>
			<Button
				variant="outlined"
				startIcon={<AddIcon />}
				onClick={(e) => setMenu(e.currentTarget)}
			>
				Add Profile Item
			</Button>
			<Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(undefined)}>
				<MenuItem onClick={() => addBlankItem("pronouns")}>Pronouns</MenuItem>
				<MenuItem onClick={() => addBlankItem("url")}>Link</MenuItem>
				<MenuItem onClick={() => addBlankItem("email")}>Email</MenuItem>
				<MenuItem onClick={() => addBlankItem("bday")}>Birthdate</MenuItem>
				<MenuItem onClick={() => addBlankItem("tel")}>Phone Number</MenuItem>
				<MenuItem onClick={() => addBlankItem("note")}>Bio</MenuItem>
			</Menu>
		</Stack>
	);
};
